import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';

export type ToastTone = 'info' | 'success' | 'error' | 'warning';

interface Toast {
  id: number;
  tone: ToastTone;
  title?: string;
  message: string;
  /** Auto-dismiss timeout in ms. Pass 0 / null to keep until dismissed. */
  ttl?: number;
}

interface ToastApi {
  show(toast: Omit<Toast, 'id'>): number;
  info(message: string, opts?: Partial<Omit<Toast, 'id' | 'tone' | 'message'>>): number;
  success(message: string, opts?: Partial<Omit<Toast, 'id' | 'tone' | 'message'>>): number;
  error(message: string, opts?: Partial<Omit<Toast, 'id' | 'tone' | 'message'>>): number;
  warning(message: string, opts?: Partial<Omit<Toast, 'id' | 'tone' | 'message'>>): number;
  dismiss(id: number): void;
}

const DEFAULT_TTL = 4500;
const ToastCtx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (input: Omit<Toast, 'id'>): number => {
      const id = nextId.current++;
      const ttl = input.ttl ?? DEFAULT_TTL;
      setToasts((prev) => [...prev, { ...input, id }]);
      if (ttl > 0) {
        setTimeout(() => dismiss(id), ttl);
      }
      return id;
    },
    [dismiss]
  );

  const api = useMemo<ToastApi>(
    () => ({
      show,
      info: (message, opts = {}) => show({ tone: 'info', message, ...opts }),
      success: (message, opts = {}) => show({ tone: 'success', message, ...opts }),
      error: (message, opts = {}) => show({ tone: 'error', message, ttl: 6500, ...opts }),
      warning: (message, opts = {}) => show({ tone: 'warning', message, ...opts }),
      dismiss
    }),
    [show, dismiss]
  );

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <Viewport toasts={toasts} onDismiss={dismiss} />
    </ToastCtx.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

const TONE_STYLES: Record<ToastTone, string> = {
  info: 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 ring-slate-700/50 dark:ring-slate-200/50',
  success: 'bg-emerald-600 text-white ring-emerald-700/50',
  error: 'bg-red-600 text-white ring-red-700/50',
  warning: 'bg-amber-500 text-white ring-amber-600/50'
};

function Viewport({ toasts, onDismiss }: { toasts: Toast[]; onDismiss(id: number): void }) {
  return (
    <div
      className="fixed inset-x-0 bottom-0 sm:bottom-4 sm:right-4 sm:left-auto z-50 flex flex-col gap-2 p-4 sm:p-0 pointer-events-none"
      aria-live="polite"
      aria-relevant="additions"
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss(): void }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setOpen(true), 10);
    return () => clearTimeout(t);
  }, []);
  return (
    <div
      role="status"
      className={`pointer-events-auto rounded-lg shadow-lg ring-1 px-4 py-3 max-w-md transition transform ${
        TONE_STYLES[toast.tone]
      } ${open ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          {toast.title && <p className="text-sm font-semibold">{toast.title}</p>}
          <p className="text-sm break-words">{toast.message}</p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="text-current/80 hover:text-current text-lg leading-none"
        >
          ×
        </button>
      </div>
    </div>
  );
}
